import { gql } from "@apollo/client";
import { defaultServiceLineSchedulerConfig, defaultVisitTypeSchedulerConfig, ServiceLineScheduleConfig, VisitTypeScheduleConfig } from "@caredna-marlin/utils/configurationTypes";
import { groupBy, keyBy, omit, pick } from "lodash";
import { createContext, PropsWithChildren, useContext, useMemo } from "react";
import { useOrgConfigQuery } from "~/graphql/__generated__/graphql";
import { useAuth } from "~/state/auth";

type Data = NonNullable<ReturnType<typeof useOrgConfigQuery>['data']>
type PRecord<T> = Record<string, T | undefined>

const createOrgConfig = (data: Data | undefined, loading: boolean) => {
  const providerTypes = keyBy(data?.providerType || [], "id");

  const vrrRows = data?.visitResourceRequirement.map((vrr) => ({
    ...omit(vrr, "visitResourceRequirementPT"),
    providerTypes: vrr.visitResourceRequirementPT?.map(vrrpt => providerTypes[vrrpt.providerTypeId])}
  )) || [];
  // Keep the long-standing client VRR shape stable for visit consumers. The
  // errand foreign key is only needed here to associate requirements.
  const vrrs = vrrRows.map(vrr => omit(vrr, "errandTypeId"));
  const vrrByVisitType = groupBy(vrrs, "visitTypeId") as PRecord<typeof vrrs>;
  const vrrByErrandType = groupBy(vrrRows, "errandTypeId");

  const parseOrEmpty = (input?: string | null) => {
    try {
      return JSON.parse(input || "{}");
    }
    catch(error) {
      console.error(`Malformed org configuration: ${input}`)
    }
    return {};
  }

  // For both visit type and service line config:
  // Take the default config + the one loaded from the server.
  // `pick` throws away any keys that don't match our current setup; basically, this ensures
  // that we always have only valid values, even though the value we get
  // from the resolver (auto-generated by drizzle) may give us JSON that doesn't match
  // the type.
  const visitTypes = data?.visitType.map(vt => ({
    ...vt,
    visitResourceRequirements: vrrByVisitType[vt.id] || [],

    schedulingConfig: pick({
      ...defaultVisitTypeSchedulerConfig,
      ...parseOrEmpty(vt.schedulingConfig)
    }, Object.keys(defaultVisitTypeSchedulerConfig)) as VisitTypeScheduleConfig,
  })) || [];

  const serviceLines = data?.serviceLine.map(sl => ({
    ...sl,
    schedulingConfig: pick({
      ...defaultServiceLineSchedulerConfig,
      ...parseOrEmpty(sl.schedulingConfig)
    }, Object.keys(defaultServiceLineSchedulerConfig)) as ServiceLineScheduleConfig,
  })) || [];

  const errandTypes = data?.errandType.map(errandType => ({
    ...errandType,
    visitResourceRequirements: (vrrByErrandType[errandType.id] || [])
      .map(requirement => omit(requirement, "errandTypeId")),
  })) || [];
  const errandTypesById = keyBy(errandTypes, "id") as PRecord<typeof errandTypes[number]>;

  // Errand resources only carry a requirement id, so consumers need a way back to the errand they belong to.
  const errandTypeByRequirementId = Object.fromEntries(
    vrrRows.flatMap(vrr => {
      const errandType = vrr.errandTypeId ? errandTypesById[vrr.errandTypeId] : undefined;
      return errandType ? [[vrr.id, errandType] as const] : [];
    })
  ) as PRecord<typeof errandTypes[number]>;

  // The GQL return type for address coordinates is string | null | undefined, which is very inconvenient for sharing code
  // between the client and server (where it's never undefined).
  const shiftCenters = data?.shiftCenter.map(sc => ({ ...sc, addressCoordinates: sc.addressCoordinates ?? null })) || [];

  const eligibilityQuestions = (data?.eligibilityQuestion || []).map(question => ({
    ...question,
    programEligibilityQuestions: question.programEligibilityQuestions.map((link) => ({
      programId: link.programId,
      disqualifyingAnswers: link.disqualifyingAnswers,
    })),
  }));

  return {
    // General
    loading,
    serviceLines: keyBy(serviceLines || [], "id") as PRecord<typeof serviceLines[number]>,
    GOOGLE_MAPS_WEB_API_KEY: data?.secrets.GOOGLE_MAPS_WEB_API_KEY || "",
    MAPBOXGL_API_KEY: data?.secrets.MAPBOXGL_API_KEY || "",

    // Operate
    providerTypes,
    services: keyBy(data?.service || [], "id") as PRecord<Data["service"][number]>,
    supplyChangeReasons: keyBy(data?.supplyChangeReason || [], "id") as PRecord<Data["supplyChangeReason"][number]>,
    shiftCenters: keyBy(shiftCenters || [], "id") as PRecord<typeof shiftCenters[number]>,
    visitCancellationReasons: keyBy(data?.visitCancellationReason || [], "id") as PRecord<Data["visitCancellationReason"][number]>,
    visitTypes: keyBy(visitTypes, "id") as PRecord<typeof visitTypes[number]>,
    visitResourceRequirements: keyBy(vrrs || [], "id") as PRecord<NonNullable<typeof vrrs>[number]>,
    errandTypes: errandTypesById,
    errandTypeByRequirementId,
    teamProvider: data?.teamProvider,
    precautions: keyBy(data?.precaution || [], "id") as PRecord<Data["precaution"][number]>,

    // Predict
    programs: keyBy(data?.program || [], "id") as PRecord<Data["program"][number]>,
    eligibilityQuestions: keyBy(eligibilityQuestions, "id") as PRecord<typeof eligibilityQuestions[number]>,
  } as const;
};

type OrgConfigContextType = ReturnType<typeof createOrgConfig>;

const OrgConfigContext = createContext<OrgConfigContextType>({
  loading: false,
  providerTypes: {},
  services: {},
  shiftCenters: {},
  serviceLines: {},
  supplyChangeReasons: {},
  visitCancellationReasons: {},
  visitTypes: {},
  visitResourceRequirements: {},
  errandTypes: {},
  errandTypeByRequirementId: {},
  GOOGLE_MAPS_WEB_API_KEY: "",
  MAPBOXGL_API_KEY: "",
  teamProvider: undefined,
  programs: {},
  eligibilityQuestions: {},
  precautions: {},
});

export const useOrgConfig = () => useContext(OrgConfigContext)!;

export const OrgConfigProvider = ({ children }: PropsWithChildren) => {
  const { isAuthenticated } = useAuth();
  const { data, loading } = useOrgConfigQuery( {
    skip: !isAuthenticated,
  });

  const orgConfig = useMemo(() => {
    return createOrgConfig(data, loading);
  }, [data, loading])

  return (
    <OrgConfigContext.Provider value={orgConfig}>
      {children}
    </OrgConfigContext.Provider>
  )
}

export type OrgConfig = ReturnType<typeof useOrgConfig>;
export type ProviderType = NonNullable<OrgConfig["providerTypes"][string]>;
export type VisitType = NonNullable<OrgConfig["visitTypes"][string]>;
export type ServiceLine = NonNullable<OrgConfig["serviceLines"][string]>;
export type Service = NonNullable<OrgConfig["services"][string]>;
export type VisitResourceRequirement = NonNullable<OrgConfig["visitResourceRequirements"][string]>;
export type ErrandType = NonNullable<OrgConfig["errandTypes"][string]>;
export type ShiftCenter = NonNullable<OrgConfig["shiftCenters"][string]>;
export type Program = NonNullable<OrgConfig["programs"][string]>;
export type EligibilityQuestion = NonNullable<OrgConfig["eligibilityQuestions"][string]>;
export type PrecautionConfig = NonNullable<OrgConfig["precautions"][string]>;

gql(`
  query OrgConfig
  @permission(requires: useWebApp)
  {
    serviceLine(orderBy: {name: { direction: asc, priority: 1 }}) {
      id
      name
      schedulingConfig
      visitTypeServiceLines {
        id
        visitTypeId
      }
      errandTypeServiceLines {
        id
        errandTypeId
      }
    }
    visitType(orderBy: {name: { direction: asc, priority: 1 }}) {
      id
      name
      schedulingConfig
      visitTypeServices {
        id
        selectedByDefault
        service {
          id
          name
          duration
          active
        }
      }
    }
    visitResourceRequirement(orderBy: {id: { direction: asc, priority: 1 }}) {
      id
      duration
      inHome
      invitedToVisit
      offset
      visitTypeId
      errandTypeId
      requiresContinuityOfCare
      preserveAutoschedulerGroup
      visitResourceRequirementPT {
        providerTypeId
      }
    }
    errandType(orderBy: {name: { direction: asc, priority: 1 }}) {
      id
      name
      description
      relativeToVisit
      active
    }
    program(orderBy: {name: { direction: asc, priority: 1 }}) {
      id
      name
      serviceLineId
    }
    eligibilityQuestion(orderBy: {id: { direction: asc, priority: 1 }}) {
      id
      question
      answerFormatType
      answerModule
      answerModulePredicate
      useForPreScoreCheck

      answerOptions {
        id
        answer
      }

      programEligibilityQuestions(orderBy: {programId: { direction: asc, priority: 1 }}) {
        programId
        disqualifyingAnswers
      }
    }
    service(orderBy: {name: { direction: asc, priority: 1 }}) {
      id
      name
      description
      duration
      active
    }
    providerType(orderBy: {name: { direction: asc, priority: 1 }}) {
      id
      name
      requireStateLicense
    }
    shiftCenter(orderBy: {id: { direction: asc, priority: 1 }}) {
      id
      name
      organizationalUnitId
      addressStreet
      addressLineTwo
      addressCity
      addressState
      addressZip
      addressCoordinates
      addressTimezone
    }
    secrets {
      GOOGLE_MAPS_WEB_API_KEY
      MAPBOXGL_API_KEY
    }
    teamProvider {
      id
      teamId
      providerId
      team {
        id
        name
      }
    }
    visitCancellationReason {
      id
      description
      reasonType
      active
    }
    supplyChangeReason {
      id
      label
      eventType
      active
    }
    precaution(orderBy: {name: { direction: asc, priority: 1 }}) {
      id
      name
      color
      description
      active
    }
  }
`);
